import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { firstValueFrom, Observable } from 'rxjs';
import { take } from 'rxjs/operators';

import { LocalStorageKey } from '@shared/constants/localStorageKey';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { AuthQuery } from '@shared/store/auth/auth.query';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  private authQuery = inject(AuthQuery);
  private router = inject(Router);

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | UrlTree {
    return this.isAuthenticated(route, state);
  }

  canActivateChild(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | UrlTree {
    return this.canActivate(route, state);
  }

  async isAuthenticated(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    const isLoggedIn = await firstValueFrom(this.authQuery.isLoggedIn$.pipe(take(1)));

    if (isLoggedIn) {
      const lastURL = localStorage.getItem(LocalStorageKey.LOGIN_LAST_URL);

      if (lastURL) {
        const [url, ...rest] = lastURL.split('?');
        const urlSearchParams = new URLSearchParams(`?${rest.join('?')}`);
        const params = Object.fromEntries(urlSearchParams.entries());

        localStorage.removeItem(LocalStorageKey.LOGIN_LAST_URL);

        return this.router.createUrlTree([`${url}`], { queryParams: params });
      }

      return true;
    }

    if (localStorage.getItem(LocalStorageKey.MANUAL_LOG_OUT)) {
      localStorage.removeItem(LocalStorageKey.MANUAL_LOG_OUT);
    } else {
      localStorage.setItem(LocalStorageKey.LOGIN_LAST_URL, state.url);
    }

    return this.router.createUrlTree([`/${ROUTING_PATH.LOGIN}`]);
  }
}
